import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IoNotificationsOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 9998; /* sit above most UI but below dropdown */
`;

const BellButton = styled.button`
  background: transparent;
  border: none;
  color: #dfbd69;
  cursor: pointer;
  font-size: 24px;
`;

const Dropdown = styled.div`
  /* use fixed so it escapes header stacking contexts */
  position: fixed;
  right: 16px;
  top: 56px;
  width: 360px;
  max-height: 60vh;
  overflow-y: auto;
  background: #0b0b0b;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 28px 8px 8px 8px;
  box-shadow: 0 8px 48px rgba(0, 0, 0, 0.75);
  z-index: 100000; /* very high to sit above other UI */
  pointer-events: auto;
`;

const NotifRow = styled.div`
  padding: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.03);
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const NotifText = styled.div`
  flex: 1;
  color: #dfbd69;
`;

const DeleteBtn = styled.button`
  background: rgba(255, 128, 128, 0.06);
  border: 1px solid rgba(255, 128, 128, 0.2);
  color: #ff8080;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 50%;
  font-weight: bold;
  cursor: pointer;
  line-height: 1;
`;

const Empty = styled.div`
  color: #cfcfcf;
  padding: 12px;
  text-align: center;
`;
const CloseBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(223,189,105,0.15);
  color: #DFBD69;
  border: 1px solid #DFBD69;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  backdrop-filter: blur(3px);
  z-index: 1;
`;

const STORAGE_KEY = "deletedNotifs_v1";

export default function NotificationsModal({ autoOpen = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(autoOpen);
  const [notifs, setNotifs] = useState([]);
  const [deleted, setDeleted] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    fetch("https://xianhefairy.com/LotusFairy_API/api/profile/GetNotifs")
      .then((res) => res.json())
      .then((data) => {
        if (!mounted) return;
        // filter out deleted IDs and those with NotifStatus === 5
        const visible = (data || []).filter(
          (n) => !deleted.includes(n.ID) && Number(n.NotifStatus) !== 5
        );
        setNotifs(visible);
      })
      .catch((err) => console.error("Failed to fetch notifications", err));

    return () => {
      mounted = false;
    };
  }, [open, deleted]);

  // position state for dropdown (viewport coordinates)
  const bellRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const [position, setPosition] = useState({ top: 56, left: null, width: 360 });

  const computePosition = () => {
    const bellEl = bellRef.current;
    const ddWidth = dropdownRef.current ? dropdownRef.current.offsetWidth : position.width;
    if (!bellEl) return;
    const rect = bellEl.getBoundingClientRect();
    // prefer centered under bell, clamp to viewport
    const centeredLeft = rect.left + rect.width / 2 - ddWidth / 2;
    const minLeft = 8;
    const maxLeft = Math.max(window.innerWidth - ddWidth - 8, minLeft);
    const left = Math.min(Math.max(centeredLeft, minLeft), maxLeft);
    const top = rect.bottom + 8; // small gap under bell
    setPosition({ top, left, width: ddWidth });
  };

  useEffect(() => {
    if (!open) return;
    computePosition();
    const onResize = () => computePosition();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDelete = (id) => {
    const newDeleted = [...deleted, id];
    setDeleted(newDeleted);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newDeleted));
    setNotifs((s) => s.filter((n) => n.ID !== id));
  };

  return (
    <div style={{ position: "relative" }}>
      <BellButton
        ref={bellRef}
        aria-label="Notifications"
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
      >
        <IoNotificationsOutline />
      </BellButton>

      {open && (
        <Overlay>
          <Dropdown
            ref={dropdownRef}
            style={{ top: position.top, left: position.left, width: position.width, position: "fixed" }}
          >
            <CloseBtn aria-label="Close notifications" onClick={() => setOpen(false)}>
              {t('notifs.close')} ✕
            </CloseBtn>
            {notifs.length === 0 ? (
              <Empty>{t('notifs.noNotifications')}</Empty>
            ) : (
              notifs.map((n) => (
                <NotifRow key={n.ID}>
                  <NotifText>
                    <div style={{ fontWeight: "bold" }}>{n.Title}</div>
                    <div style={{ fontSize: 13, color: "#cfcfcf" }}>{n.Description}</div>
                    <div style={{ fontSize: 11, color: "#999" }}>{new Date(n.EffectDate).toLocaleString()}</div>
                  </NotifText>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <DeleteBtn aria-label={`Delete notification ${n.ID}`} onClick={() => handleDelete(n.ID)}>
                      ×
                    </DeleteBtn>
                  </div>
                </NotifRow>
              ))
            )}
          </Dropdown>
        </Overlay>
      )}
    </div>
  );
}
